import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './FKP.css';

// Import Images
import keycloakIcon from '../Public/Images/keycloak.png';
import harborIcon from '../Public/Images/harbor.png';
import grafanaIcon from '../Public/Images/grafanaNoBackground.png';
import argoCdIcon from '../Public/Images/argo.png';
import clusterAutoscalerIcon from '../Public/Images/cluster-autoscaler.png';
import eupraxiaLabsLogo from '../Public/Images/Eupraxia_X.jpg';
import trailbossLogo from '../Public/Images/trailboss.png';
import outPostLogo from '../Public/Images/ff_outpost_logo.png';

function FKP() {
    const [modal, setModal] = useState(null);
    const [isDark, setIsDark] = useState(() => {
        try { return document.documentElement.getAttribute('data-theme') === 'dark'; } catch (e) { return false; }
    });
    const rootRef = useRef(null);

    useEffect(() => {
        const apply = (darkNow) => {
            try {
                if (!rootRef.current) return;
                const hero = rootRef.current.querySelector('.hero-section-fkp');
                const features = rootRef.current.querySelector('.features-section');
                const value = rootRef.current.querySelector('.value-prop-section');
                const compliance = rootRef.current.querySelector('.compliance-outcomes-section');
                const cta = rootRef.current.querySelector('.fkp-cta-section');
                if (darkNow) rootRef.current.classList.add('dark'); else rootRef.current.classList.remove('dark');
                const els = [hero, features, value, compliance, cta];
                if (darkNow) {
                    els.forEach(el => {
                        if (!el) return;
                        el.style.setProperty('background', '#16171a', 'important');
                        el.style.setProperty('background-image', 'none', 'important');
                        el.style.setProperty('color', '#e6eef8', 'important');
                    });
                    // also set feature cards explicitly
                    Array.from(rootRef.current.querySelectorAll('.feature')).forEach(f => {
                        f.style.setProperty('background', '#0b0b0b', 'important');
                        f.style.setProperty('color', '#e5e5e5', 'important');
                    });
                } else {
                    els.forEach(el => {
                        if (!el) return;
                        el.style.removeProperty('background');
                        el.style.removeProperty('background-image');
                        el.style.removeProperty('color');
                    });
                    Array.from(rootRef.current.querySelectorAll('.feature')).forEach(f => {
                        f.style.removeProperty('background');
                        f.style.removeProperty('color');
                    });
                }
            } catch (e) {}
        };
        const obs = new MutationObserver(() => {
            const darkNow = document.documentElement.getAttribute('data-theme') === 'dark';
            setIsDark(darkNow);
            apply(darkNow);
        });
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        // initial
        apply(document.documentElement.getAttribute('data-theme') === 'dark');
        return () => obs.disconnect();
    }, []);

    const features = [
        {
            id: 'capi',
            icon: clusterAutoscalerIcon,
            title: 'Cluster API (CAPI) Foundation',
            description: 'Cluster API provides a consistent way to deploy, scale, and manage Kubernetes clusters across clouds, on-prem, or hybrid environments.',
            value: [
                'Audit Advantage: Every cluster deployment is standardized and reproducible, reducing audit complexity and drift.',
                'Faster Compliance: Automated provisioning eliminates human error and creates clear, documentable evidence for compliance reports.',
                'Future-Proof: Agencies and enterprises avoid vendor lock-in with a CNCF-backed standard.'
            ]
        },
        {
            id: 'trailboss',
            icon: trailbossLogo,
            title: 'TrailBoss',
            description: 'TrailBoss is Eupraxia Labs’ API orchestration engine, unifying multiple infrastructure services under one control plane.',
            value: [
                'Centralized Control: Compliance evidence (logs, metrics, configs) can be pulled from one place.',
                'Reduced Audit Costs: Instead of reconciling dozens of siloed services, auditors see a single orchestrated record of truth.',
                'Efficiency: Fewer engineering hours spent managing services = lower OPEX.'
            ]
        },
        {
            id: 'outpost',
            icon: outPostLogo,
            title: 'Outpost',
            description: 'Outpost is the graphical interface for VitroAI™, built with Vue.js and CoreUI for simplicity and speed.',
            value: [
                'Ease of Use: Administrators can configure complex environments without scripting everything by hand.',
                'Audit Ready: Pre-built dashboards for compliance reporting and system state visibility.',
                'Broader Adoption: Non-Kubernetes experts (IT admins, compliance teams) can see, validate, and document environments directly.'
            ]
        },
        {
            id: 'keycloak',
            icon: keycloakIcon,
            title: 'Keycloak',
            description: 'Keycloak provides enterprise-grade identity and access management, with SSO, MFA, and RBAC out of the box.',
            value: [
                'Zero Trust Alignment: Enforce least privilege access across workloads.',
                'Audit Trail: Every login, role change, and access attempt is logged for compliance mapping.',
                'Faster Certifications: HIPAA, PCI-DSS, and SOC 2 all require strong IAM controls — VitroAI™ ships with them pre-integrated.'
            ]
        },
        {
            id: 'harbor',
            icon: harborIcon,
            title: 'Harbor',
            description: 'Harbor is a cloud-native container registry with image signing, scanning, and RBAC.',
            value: [
                'Built-In Security: Automatically scans containers for vulnerabilities, helping prove compliance with NIST, SOC 2, and PCI-DSS.',
                'Supply Chain Transparency: SBOM support ensures customers can trace software provenance (critical for FedRAMP & ISO 27001).',
                'Confidence for DevOps: Teams can move faster knowing every image is policy-enforced.'
            ]
        },
        {
            id: 'argocd',
            icon: argoCdIcon,
            title: 'ArgoCD + GitLab',
            description: 'ArgoCD delivers declarative GitOps for Kubernetes, while GitLab provides CI/CD pipelines, code security, and developer workflows.',
            value: [
                'Immutable Audit Trails: Every change is tracked in Git, making compliance reporting automatic instead of manual.',
                'Continuous Security: Integrate code scanning, IaC checks, and deployment policies directly in pipelines.',
                'DevSecOps Acceleration: Teams ship faster while maintaining compliance guardrails.'
            ]
        },
        {
            id: 'observability',
            icon: grafanaIcon,
            title: 'Observability Suite',
            description: 'Built-in Grafana stack for metrics, logging, and tracing, providing comprehensive visibility into Federal Frontier Operational readiness.',
            value: [
                'Real-Time Insights: Monitor performance and troubleshoot issues with live dashboards.',
                'Compliance Visibility: Centralized logs and traces simplify audit reporting for NIST and SOC 2.',
                'Operational Efficiency: Proactive monitoring reduces downtime and enhances team productivity.'
            ]
        },
        {
            id: 'capi-visualizer',
            icon: clusterAutoscalerIcon, // Reuse or import a specific icon if available
            title: 'FMC Visualizer',
            description: 'Turn complex Kubernetes Cluster API deployments into intuitive, interactive diagrams. The CAPI Visualizer makes cluster relationships, resources, and scaling activity instantly understandable—empowering teams to troubleshoot faster, plan smarter, and manage at scale with confidence.',
            value: [
                'Visual Lifecycle Mapping: Diagram cluster provisioning, scaling, and upgrades for easier understanding and troubleshooting.',
                'Compliance Documentation Aid: Generate exportable diagrams for audit trails and stakeholder reports.',
                'Integration with Outpost: Embed visualizations in the GUI for real-time deployment monitoring and decision-making.'
            ]
        }
    ];

    return (
        <div ref={rootRef} className={`fkp-page ${isDark ? 'dark' : ''}`}>
            <section className="hero-section-fkp">
                <div className="hero-content">
                    <h1>Federal Frontier Kubernetes Platform™ (FKP)</h1>
                    <p>Eupraxia Labs’ FKP™ is a subscription-based Kubernetes management solution designed for cloud, on-premise, and edge. Built on Cluster API, it gives teams an open, flexible way to manage clusters without the lock-in and overhead of proprietary platforms.</p>
                </div>
            </section>
            <section className="features-section">
                <h2>Core Features</h2>
                <div className="features-container">
                    {features.map((feature) => (
                        <div
                            key={feature.id}
                            className="feature"
                            onClick={() => setModal(feature)}
                        >
                            <img src={feature.icon} alt={feature.title} className="feature-icon" style={feature.id === 'trailboss' ? { width: '50px', height: '50px', objectFit: 'contain' } : {}} />
                            <h3>{feature.title}</h3>
                            <p>{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>
            <section className="compliance-outcomes-section">
                <h2>Why This Matters for Compliance & Business Outcomes</h2>
                <h3>For Government Agencies:</h3>
                <ul>
                    <li>Map controls directly to NIST 800-53 and DISA STIGs using pre-integrated logging and IAM.</li>
                    <li>Cut ATO timelines with reproducible CAPI-driven deployments and GitOps governance.</li>
                    <li>Reduce compliance costs by centralizing audit data across TrailBoss and Outpost.</li>
                </ul>
                <h3>For Private Sector Enterprises:</h3>
                <ul>
                    <li>Prove HIPAA/HITECH readiness with Keycloak IAM, encrypted workloads, and VDI.</li>
                    <li>Achieve PCI-DSS faster with Harbor scanning + audit-ready ArgoCD pipelines.</li>
                    <li>Simplify SOC 2 & ISO 27001 evidence gathering with built-in observability + GitOps.</li>
                </ul>
                <p>Federal Frontier Kubernetes Platform™ and VitroAI™ doesn’t just give you infrastructure — it gives you the built-in controls and audit evidence to pass compliance faster.</p>
            </section>
            <section className="value-prop-section">
                <h2>Value Proposition</h2>
                <ul>
                    <li>Avoids vendor lock-in</li>
                    <li>Fully open source–driven</li>
                    <li>Cost-optimized for federal and enterprise deployments</li>
                    <li>Secure & compliant by design</li>
                </ul>
            </section>
            <section className="fkp-cta-section">
                <p>FKP™ gives you the freedom to run Kubernetes your way — securely, openly, and cost-effectively.</p>
                <Link to="/contact" className="fkp-learn-more-button">See FKP™ in Action</Link>
            </section>
            {modal && (
                <div className="modal-overlay" onClick={() => setModal(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
                        <div className="modal-title">{modal.title}</div>
                        <div className="modal-description">{modal.description}</div>
                        <div className="modal-value">
                            <ul>
                                {modal.value.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FKP;